import { useMemo } from 'react';

import formatDate from '@/utils/formatDate.ts';
import { CANDIDATE_STATUS_TOOLTIP } from '@/utils/patientStatus';

interface PatientStatusTooltipProps {
  status?: string | null;
  statusReason?: string | null;
  statusStartDate?: string | Date | null;
  statusEndDate?: string | Date | null;
  statusUpdatedAt?: string | Date | null;
}

const usePatientStatusTooltip = (patient: PatientStatusTooltipProps | null) =>
  useMemo(() => {
    if (!patient) return { shouldShow: false, items: [] };

    const { status, statusReason, statusStartDate, statusEndDate } = patient;
    const statusKey = status?.trim().toLowerCase();

    const hasText = (value?: string | null) => {
      const text = (value ?? '').trim();
      return text !== '' && text !== '-' && text !== '—';
    };

    // Active has no reason of its own. A status date is not enough to show the icon.
    const shouldHide =
      statusKey === 'active' ||
      (statusKey === 'inactive' && !hasText(statusReason)) ||
      (statusKey === 'paused' && !hasText(statusReason) && !statusStartDate);

    if (shouldHide) return { shouldShow: false, items: [] };

    const items = (() => {
      switch (statusKey) {
        case 'inactive':
          return hasText(statusReason) ? [{ label: 'Reason', value: statusReason as string }] : [];
        case 'candidate':
          return [{ label: 'Pending', value: CANDIDATE_STATUS_TOOLTIP }];
        case 'paused': {
          const pauseItems = [];
          if (statusStartDate) {
            pauseItems.push({
              label: 'Pause Date',
              value: `${formatDate(statusStartDate)} until ${formatDate(statusEndDate)}`
            });
          }
          if (statusReason) {
            pauseItems.push({ label: 'Reason', value: statusReason });
          }
          return pauseItems;
        }
        default:
          return [];
      }
    })();

    const visible = items.filter((item) => hasText(item.value));
    if (!visible.length) return { shouldShow: false, items: [] };
    return { shouldShow: true, items: visible };
  }, [patient]);

export default usePatientStatusTooltip;
